// SCR-STAFF-960 (AP-ENG-01؛ US-ENG-001 تا 005): سرنخ‌ها — فهرست با جستجوی نام یا موبایل، وضعیت و «سرنخ‌های من»، کانبان وضعیت (SM-Lead) و ثبت سریع
// (Alt+L). جزئیات در /crm/leads/:id.
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type EngLeadStatus, type EngLeadSummaryView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, JalaliDate, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebounced } from '../patients/common';
import { WriteNotice } from '../settings/common';
import { engKeys, LEAD_STATUSES, leadPath, leadTone } from './common';
import { LeadAssignmentSettingsDialog, SlaChip } from './LeadAssignment';
import { LeadDialog } from './LeadDialog';

export const SCR_STAFF_960 = 'SCR-STAFF-960';

/** میان‌بر «سرنخ تازه» (FLOW-15: Alt+L). */
export function useNewLeadShortcut(onOpen: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.altKey && (event.key === 'l' || event.key === 'L' || event.code === 'KeyL')) {
        event.preventDefault();
        onOpen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpen, enabled]);
}

export function LeadsPage() {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const navigate = useNavigate();
  const write = useWriteAccess('eng.lead.manage');
  const [view, setView] = useState<'list' | 'board'>('list');
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 250);
  const [status, setStatus] = useState<EngLeadStatus | ''>('');
  const [mine, setMine] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const access = useAccess();
  const canAssign = access.can('eng.lead.assign');
  useNewLeadShortcut(() => setCreating(true), write.canWrite);

  const leads = useQuery({
    queryKey: [...engKeys.leads(session?.tenantId), 'list', q, status, mine, cursor],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/leads', { params: { query: { q: q || undefined, status: status || undefined, mine, cursor, limit: 50 } } })),
    enabled: view === 'list',
    placeholderData: keepPreviousData,
  });
  const board = useQuery({
    queryKey: [...engKeys.board(session?.tenantId), q, mine],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/leads/board', { params: { query: { q: q || undefined, mine, limit: 20 } } })),
    enabled: view === 'board',
  });

  const columns: DataTableColumn<EngLeadSummaryView>[] = [
    {
      id: 'name',
      headerKey: 'eng:leads.columns.name',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{row.fullName}</span>
          {row.isExistingPatient && <Chip size="small" color="info" variant="outlined" label={t('eng:leads.existing_patient')} />}
          <SlaChip lead={row} testId={`lead-sla-${row.id}`} />
        </Stack>
      ),
    },
    { id: 'mobile', headerKey: 'eng:leads.columns.mobile', cell: (row) => <span dir="ltr">{faDigits(row.mobile)}</span> },
    { id: 'source', headerKey: 'eng:leads.columns.source', cell: (row) => row.sourceName ?? '—' },
    { id: 'interest', headerKey: 'eng:leads.columns.interest', cell: (row) => row.interestName ?? '—' },
    { id: 'status', headerKey: 'eng:leads.columns.status', cell: (row) => <StatusChip tone={leadTone(row.status)} labelKey={`eng:lead_status.${row.status}`} /> },
    { id: 'owner', headerKey: 'eng:leads.columns.owner', cell: (row) => row.ownerName ?? '—' },
    { id: 'next', headerKey: 'eng:leads.columns.next_due', cell: (row) => (row.nextDueAt ? <JalaliDate value={row.nextDueAt} withTime /> : '—') },
    { id: 'created', headerKey: 'eng:leads.columns.created', cell: (row) => <JalaliDate value={row.createdAt} /> },
    {
      id: 'actions',
      headerKey: 'eng:leads.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => navigate(leadPath(row.id))} data-testid={`lead-open-${row.id}`}>
          {t('eng:leads.open')}
        </Button>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_960}
      titleKey="eng:leads.title"
      subtitleKey="eng:leads.subtitle"
      actions={
        <>
          {canAssign && (
            <Button onClick={() => setConfiguring(true)} data-testid="lead-assignment-settings">
              {t('eng:assignment.open')}
            </Button>
          )}
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={!write.canWrite} data-testid="lead-create" aria-keyshortcuts="Alt+L">
          {t('eng:leads.add')}
        </Button>
        </>
      }
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Tabs value={view} onChange={(_event, next: 'list' | 'board') => setView(next)} aria-label={t('eng:leads.view_label')}>
            <Tab value="list" label={t('eng:leads.view_list')} data-testid="lead-view-list" />
            <Tab value="board" label={t('eng:leads.view_board')} data-testid="lead-view-board" />
          </Tabs>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              label={t('eng:leads.search')}
              value={text}
              onChange={(event) => {
                setCursor(undefined);
                setText(event.target.value);
              }}
              inputProps={{ 'data-testid': 'lead-search' }}
              sx={{ minWidth: 240 }}
            />
            {view === 'list' && (
              <TextField
                select
                size="small"
                label={t('eng:leads.status_filter')}
                value={status}
                onChange={(event) => {
                  setCursor(undefined);
                  setStatus(event.target.value as EngLeadStatus | '');
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">{t('eng:common.all')}</MenuItem>
                {LEAD_STATUSES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`eng:lead_status.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <FormControlLabel control={<Switch checked={mine} onChange={(event) => setMine(event.target.checked)} />} label={t('eng:leads.mine')} />
          </Stack>
        </Stack>
      }
    >
      {view === 'list' ? (
        <DataTable
          captionKey="eng:leads.caption"
          columns={columns}
          rows={leads.data?.items}
          getRowId={(row) => row.id}
          isLoading={leads.isPending}
          error={leads.error}
          onRetry={() => void leads.refetch()}
          hasMore={Boolean(leads.data?.nextCursor)}
          onLoadMore={() => setCursor(leads.data?.nextCursor ?? undefined)}
          emptyTitleKey="eng:leads.empty"
        />
      ) : board.error ? (
        <ErrorState error={board.error} onRetry={() => void board.refetch()} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 1.5 }} data-testid="lead-board">
          {(board.data?.columns ?? []).map((column) => (
            <Paper key={column.status} variant="outlined" sx={{ p: 1 }} component="section" aria-label={t(`eng:lead_status.${column.status}`)} data-testid={`lead-column-${column.status}`}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <StatusChip tone={leadTone(column.status)} labelKey={`eng:lead_status.${column.status}`} />
                <Typography variant="caption">{faDigits(Number(column.count))}</Typography>
              </Stack>
              <Stack spacing={1}>
                {column.items.map((item) => (
                  <Card key={item.id} variant="outlined">
                    <CardActionArea onClick={() => navigate(leadPath(item.id))} data-testid={`lead-card-${item.id}`}>
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="body2" fontWeight={600}>
                          {item.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[item.sourceName, item.interestName].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                        {item.slaBreached && !item.firstContactAt && (
                          <Chip size="small" color="error" label={t('eng:sla.breached')} sx={{ mt: 0.5 }} data-testid={`lead-card-sla-${item.id}`} />
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}
      {configuring && <LeadAssignmentSettingsDialog onClose={() => setConfiguring(false)} />}
      {creating && (
        <LeadDialog
          onClose={(saved) => {
            setCreating(false);
            if (saved) {
              navigate(leadPath(saved.id), { state: { merged: saved.merged } });
            }
          }}
        />
      )}
    </ListPage>
  );
}
